import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { getKPIIcons, hasPercentageinKpi, KpiType } from '../../../../../../constants';
import { KpiOverviewType } from '../../../../../../hooks';
import { CurrencyFormatterOptions } from '../../../../../../hooks/useCurrencyTools';
import { Icon, Icons, Typography } from '../../../../../../stories/atoms';
import { ColumnFlexContainer, RowFlexContainer } from '../../../../../../stories/atoms/containerV2/ContainerV2';
import { homeTranslations, kpiTargetTranslations } from '../../../../../../translations';
import { formatAmountWithSeparator } from '../../../../../../utils';
import { getDefaultStatus } from '../../../../common/metricTile/util';
import { StyledForecastCard } from './ForecastCard.styles';

type ForecastCardProps = {
  kpiName: KpiType;
  kpiDetails: KpiOverviewType;
  currentValue: number | null;
  currencyFormatter?: (value: number, options?: CurrencyFormatterOptions) => string;
};

export const ForecastCard: FC<ForecastCardProps> = ({ kpiName, kpiDetails, currentValue, currencyFormatter }) => {
  const { t } = useTranslation();
  const { kpiWidgets: kpiWidgetsTranslations, notSet } = kpiTargetTranslations;

  const { averageTarget, totalTarget } = kpiDetails;

  const showProgress = currentValue !== null && totalTarget !== 0;

  const targetProgressPercent = showProgress ? (currentValue / totalTarget) * 100 : 0;
  const indicator = showProgress ? getDefaultStatus(targetProgressPercent) : null;

  const getFormattedValue = (value: number | null, type: 'target' | 'forecast') => {
    if (type === 'target' && value === null) {
      return t(notSet.key, notSet.defaultValue);
    }
    if (value !== null) {
      if (hasPercentageinKpi(kpiName)) {
        return formatAmountWithSeparator(value, '%');
      }
      if ((kpiName === 'dailyTurnOver' || kpiName === 'dailySCOTurnOver') && currencyFormatter) {
        return currencyFormatter(value);
      }

      return formatAmountWithSeparator(value);
    }
  };

  return (
    <StyledForecastCard
      display="flex"
      flexDirection="column"
      indicator={indicator}
      borderRadius={[3]}
      padding={[2]}
      gap={[1]}
      hasBorder={!indicator}
      data-testid="forecast-card"
    >
      <Icon icon={Icons[getKPIIcons(kpiName)]} size="small" />
      <Typography type="legal" margin={[0, 0, 1]}>
        {t(kpiWidgetsTranslations[kpiName].key, kpiWidgetsTranslations[kpiName].defaultValue)}
      </Typography>

      <ColumnFlexContainer flex={1} gap={[2]}>
        <RowFlexContainer flex={1} gap={[1]} alignItems="center">
          <Icon icon={Icons.TARGET} size="small" />
          <Typography type="button" textAlign="right">
            {getFormattedValue(averageTarget, 'target')}
          </Typography>
        </RowFlexContainer>

        {showProgress && indicator && (
          <StyledForecastCard
            display="flex"
            flexDirection="row"
            indicator={indicator}
            padding={[1]}
            borderRadius={[2]}
            alignItems="center"
            gap={[1]}
            justifyContent="between"
            alignSelf="end"
          >
            {!!indicator && <Icon icon={Icons[indicator]} size="small" />}

            <Typography type="legal">
              {t(homeTranslations.common.progress.key, homeTranslations.common.progress.defaultValue)}
            </Typography>

            <Typography type="legal" textAlign="right">
              {targetProgressPercent.toFixed(2) + '%'}
            </Typography>
          </StyledForecastCard>
        )}
      </ColumnFlexContainer>
    </StyledForecastCard>
  );
};
